import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { C, FONT, card, logoDot } from "../theme";
import { useMediaQuery, MOBILE_QUERY } from "../lib/useMediaQuery";
import { useToast } from "../components/Toast";
import { CodeTabs } from "../components/CodeTabs";
import { UsageChart } from "../components/UsageChart";
import { ApiReference } from "../components/ApiReference";
import { KeyRow } from "../components/KeyRow";
import {
  addDomain,
  createKey,
  deleteAccount,
  deleteKey,
  getKeys,
  getUsage,
  logout,
  openPortal,
  removeDomain,
  rotateKey,
  type CreatedKey,
  type Me,
} from "../lib/api";
import { PRICING } from "../lib/pricing";

const SERVER_URL = "https://geo.zynday.com/v1";

const QUICKSTART = {
  curl: `curl "https://geo.zynday.com/v1/search?q=Berlin" \\
  -H "Authorization: Bearer YOUR_KEY"`,
  js: `await fetch("https://geo.zynday.com/v1/search?q=Berlin",
  { headers: { Authorization: "Bearer YOUR_KEY" } }
).then(r => r.json());`,
  py: `requests.get("https://geo.zynday.com/v1/search",
  params={"q": "Berlin"},
  headers={"Authorization": "Bearer YOUR_KEY"})`,
};

export function Dashboard({ me, onLogout }: { me: Me; onLogout: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const isMobile = useMediaQuery(MOBILE_QUERY);
  const [plaintexts, setPlaintexts] = useState<Record<string, string>>({});
  const [newName, setNewName] = useState("");
  const [newDomains, setNewDomains] = useState("");
  const [urlCopied, setUrlCopied] = useState(false);
  const [portalOpen, setPortalOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteText, setDeleteText] = useState("");

  const keysQ = useQuery({ queryKey: ["keys"], queryFn: getKeys });
  const usageQ = useQuery({ queryKey: ["usage"], queryFn: getUsage });
  const keys = keysQ.data ?? [];
  const plan = PRICING[me.plan.period];

  const refresh = () => qc.invalidateQueries({ queryKey: ["keys"] });
  const stash = (k: CreatedKey) => {
    setPlaintexts((m) => ({ ...m, [k.id]: k.key }));
    navigator.clipboard?.writeText(k.key).catch(() => {});
    toast("Key created & copied — it won't be shown again");
  };

  const create = useMutation({
    mutationFn: () =>
      createKey(
        newName.trim() || "new-key",
        newDomains.split(",").map((d) => d.trim().toLowerCase()).filter(Boolean),
      ),
    onSuccess: (k) => { stash(k); setNewName(""); setNewDomains(""); refresh(); },
    onError: (e) => toast(e instanceof Error ? e.message : "Could not create key"),
  });
  const rotate = useMutation({
    mutationFn: (id: string) => rotateKey(id),
    onSuccess: (k) => { setPlaintexts((m) => ({ ...m, [k.id]: k.key })); navigator.clipboard?.writeText(k.key).catch(() => {}); toast("Key rotated — old secret revoked, new one copied"); refresh(); },
  });
  const del = useMutation({ mutationFn: (id: string) => deleteKey(id), onSuccess: () => { toast("Key deleted"); refresh(); } });
  const addDom = useMutation({ mutationFn: (v: { id: string; d: string }) => addDomain(v.id, v.d), onSuccess: refresh });
  const rmDom = useMutation({ mutationFn: (v: { id: string; d: string }) => removeDomain(v.id, v.d), onSuccess: refresh });

  const doLogout = async () => { try { await logout(); } catch { /* ignore */ } onLogout(); };
  const copyUrl = () => { navigator.clipboard?.writeText(SERVER_URL).catch(() => {}); setUrlCopied(true); setTimeout(() => setUrlCopied(false), 1600); };

  const portal = async () => {
    setPortalOpen(true);
    try {
      const { url } = await openPortal();
      window.location.href = url;
    } catch (e) {
      setPortalOpen(false);
      toast(e instanceof Error ? e.message : "Could not open billing portal");
    }
  };

  const confirmDelete = async () => {
    try { await deleteAccount(); onLogout(); }
    catch (e) { toast(e instanceof Error ? e.message : "Delete failed"); }
  };

  return (
    <div data-screen-label="Dashboard">
      <header style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, padding: isMobile ? "14px 16px" : "16px 28px", borderBottom: `1px solid ${C.line}`, position: "sticky", top: 0, background: C.bg, zIndex: 10 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 11, fontWeight: 700, color: C.textBright, fontSize: 15, flex: "none" }}>
          <span style={logoDot} />geo.zynday.com{!isMobile && <span style={{ color: C.mutedDim, fontWeight: 400, fontSize: 12, marginLeft: 8 }}>/ dashboard</span>}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: isMobile ? 10 : 18, fontSize: 13, color: C.muted, minWidth: 0 }}>
          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{me.email}</span>
          <button onClick={doLogout} style={{ flex: "none", background: "transparent", border: `1px solid ${C.line3}`, color: C.text, padding: "8px 15px", borderRadius: 5, font: `600 12px ${FONT}`, cursor: "pointer" }}>log out</button>
        </div>
      </header>

      <div style={{ maxWidth: 1080, margin: "0 auto", padding: isMobile ? "28px 16px 64px" : "40px 28px 80px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 12, marginBottom: 32 }}>
          <div>
            <div style={{ color: C.textBrightest, fontSize: 24, fontWeight: 700 }}>Welcome back</div>
            <div style={{ color: C.muted, fontSize: 13, marginTop: 5 }}>Your endpoint is live and serving requests.</div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, background: "rgba(54,224,127,.1)", border: `1px solid ${C.line3}`, color: C.ac, padding: "8px 14px", borderRadius: 20, fontSize: 12 }}>
            <span style={{ width: 7, height: 7, background: C.ac, borderRadius: "50%", boxShadow: `0 0 8px ${C.ac}` }} />
            Pro · {me.plan.status}
          </div>
        </div>

        {/* server url */}
        <div style={{ ...card, marginBottom: 20, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 16, padding: "20px 22px" }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ color: C.muted, fontSize: 12, marginBottom: 7 }}>YOUR SERVER URL</div>
            <code style={{ color: C.ac, fontSize: 15, wordBreak: "break-all" }}>{SERVER_URL}</code>
          </div>
          <button onClick={copyUrl} style={{ background: "transparent", border: `1px solid ${C.line3}`, color: C.text, padding: "9px 16px", borderRadius: 6, font: `600 12px ${FONT}`, cursor: "pointer", flex: "none" }}>{urlCopied ? "copied ✓" : "copy"}</button>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1.5fr 1fr", gap: 20, alignItems: "start" }}>
          {/* API KEYS */}
          <div style={card}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
              <span style={{ color: C.textBright, fontWeight: 700, fontSize: 15 }}>API Keys</span>
              <span style={{ color: C.mutedDim, fontSize: 12 }}>{keys.length} / 5</span>
            </div>
            <div style={{ color: C.muted, fontSize: 12, marginBottom: 16 }}>Up to 5 keys. Treat these like passwords. Restrict each key to specific domains below.</div>
            {keysQ.isLoading && <div style={{ color: C.mutedDim, fontSize: 12 }}>loading keys…</div>}
            {keys.map((k) => (
              <KeyRow
                key={k.id}
                k={k}
                plaintext={plaintexts[k.id]}
                onReveal={() => {}}
                onRotate={() => rotate.mutate(k.id)}
                onDelete={() => del.mutate(k.id)}
                onAddDomain={(d) => addDom.mutate({ id: k.id, d })}
                onRemoveDomain={(d) => rmDom.mutate({ id: k.id, d })}
              />
            ))}
            <div style={{ marginTop: 16, paddingTop: 16, borderTop: `1px solid ${C.line}` }}>
              <div style={{ display: "flex", gap: 8 }}>
                <input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="new key name (e.g. staging)" style={{ flex: 1, background: C.bg, border: `1px solid ${C.line3}`, borderRadius: 6, padding: "10px 12px", color: C.textBrightest, font: `500 12.5px ${FONT}` }} />
                <button onClick={() => {
                  if (keys.length >= 5) return toast("Key limit reached (5)");
                  if (newDomains.split(",").map((d) => d.trim()).filter(Boolean).length === 0) return toast("Add at least one allowed domain");
                  create.mutate();
                }} style={{ background: C.ac, border: "none", color: C.acInk, padding: "0 18px", borderRadius: 6, font: `700 12px ${FONT}`, cursor: "pointer" }}>+ generate</button>
              </div>
              <input value={newDomains} onChange={(e) => setNewDomains(e.target.value)} placeholder="allowed domains (required, comma-separated — e.g. app.acme.com, acme.com)" style={{ width: "100%", marginTop: 8, background: C.bg, border: `1px solid ${C.line2}`, borderRadius: 6, padding: "10px 12px", color: C.textBrightest, font: `500 12px ${FONT}` }} />
            </div>
          </div>

          {/* USAGE */}
          <div style={card}>
            <div style={{ color: C.textBright, fontWeight: 700, fontSize: 15, marginBottom: 4 }}>Usage</div>
            <div style={{ color: C.muted, fontSize: 12, marginBottom: 18 }}>Requests · last 14 days</div>
            <UsageChart bars={usageQ.data?.bars ?? Array(14).fill(0)} />
            <div style={{ display: "flex", justifyContent: "space-between", borderTop: `1px solid ${C.line}`, paddingTop: 16 }}>
              <div><div style={{ color: C.textBrightest, fontSize: 20, fontWeight: 700 }}>{(usageQ.data?.total ?? 0).toLocaleString()}</div><div style={{ color: C.muted, fontSize: 11, marginTop: 2 }}>total reqs</div></div>
              <div style={{ textAlign: "right" }}><div style={{ color: C.textBrightest, fontSize: 20, fontWeight: 700 }}>{(usageQ.data?.peak ?? 0).toLocaleString()}</div><div style={{ color: C.muted, fontSize: 11, marginTop: 2 }}>peak / day</div></div>
            </div>
          </div>

          {/* QUICKSTART */}
          <div style={card}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
              <span style={{ color: C.textBright, fontWeight: 700, fontSize: 15 }}>Quickstart</span>
              <a href="#api-docs" style={{ color: C.ac, fontSize: 12, cursor: "pointer", textDecoration: "none" }}>full API docs ↓</a>
            </div>
            <CodeTabs snippets={QUICKSTART} compact />
          </div>

          {/* BILLING */}
          <div style={card}>
            <div style={{ color: C.textBright, fontWeight: 700, fontSize: 15, marginBottom: 16 }}>Billing</div>
            <Row label="Plan" value={`Pro — €${plan.perMo}/mo · ${plan.label}`} />
            <Row label="Status" value={<span style={{ color: C.ac }}>● {me.plan.status}</span>} />
            <Row label="Next invoice" value={me.plan.nextInvoice ?? "—"} last />
            <button onClick={portal} style={{ width: "100%", background: "transparent", border: `1px solid ${C.line3}`, color: C.text, padding: 11, borderRadius: 7, font: `600 13px ${FONT}`, cursor: "pointer" }}>Manage subscription ↗</button>
          </div>
        </div>

        <ApiReference />

        {/* DANGER ZONE */}
        <div style={{ background: C.panel, border: `1px solid ${C.dangerLine}`, borderRadius: 10, padding: 22, marginTop: 20, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 20 }}>
          <div>
            <div style={{ color: "#e0a0a0", fontWeight: 700, fontSize: 14, marginBottom: 5 }}>Delete account</div>
            <div style={{ color: "#8a7575", fontSize: 12, lineHeight: 1.6 }}>Permanently removes your keys, cancels billing, and revokes access. This cannot be undone.</div>
          </div>
          <button onClick={() => setDeleteOpen(true)} style={{ flex: "none", background: "transparent", border: `1px solid ${C.dangerLine2}`, color: C.danger, padding: "11px 18px", borderRadius: 7, font: `600 13px ${FONT}`, cursor: "pointer" }}>Delete account</button>
        </div>
      </div>

      {portalOpen && (
        <div style={modalBackdrop} onClick={() => setPortalOpen(false)}>
          <div style={{ background: C.panel, border: `1px solid ${C.line2}`, borderRadius: 12, padding: "48px 40px", textAlign: "center", width: 360 }}>
            <div style={{ width: 34, height: 34, border: `3px solid ${C.line2}`, borderTopColor: C.acRaw, borderRadius: "50%", margin: "0 auto 20px", animation: "spin .8s linear infinite" }} />
            <div style={{ color: C.textBright, fontWeight: 600 }}>Opening billing portal…</div>
            <div style={{ color: C.muted, fontSize: 12, marginTop: 8 }}>Redirecting to Stripe</div>
          </div>
        </div>
      )}

      {deleteOpen && (
        <div style={{ ...modalBackdrop, padding: 20 }}>
          <div style={{ background: C.panel, border: `1px solid ${C.dangerLine}`, borderRadius: 12, padding: 30, width: 420, maxWidth: "100%" }}>
            <div style={{ color: "#e0a0a0", fontWeight: 700, fontSize: 17, marginBottom: 10 }}>Delete your account?</div>
            <div style={{ color: C.mutedSoft, fontSize: 13, lineHeight: 1.7, marginBottom: 20 }}>
              This revokes all API keys, cancels your Stripe subscription, and deletes your data. Type <span style={{ color: C.danger, fontWeight: 700 }}>DELETE</span> to confirm.
            </div>
            <input value={deleteText} onChange={(e) => setDeleteText(e.target.value)} placeholder="DELETE" style={{ width: "100%", background: C.bg, border: `1px solid ${C.dangerLine2}`, borderRadius: 7, padding: 12, color: C.textBrightest, font: `500 13px ${FONT}`, marginBottom: 18 }} />
            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={() => { setDeleteOpen(false); setDeleteText(""); }} style={{ flex: 1, background: "transparent", border: `1px solid ${C.line3}`, color: C.text, padding: 12, borderRadius: 7, font: `600 13px ${FONT}`, cursor: "pointer" }}>cancel</button>
              <button onClick={confirmDelete} disabled={deleteText !== "DELETE"} style={{ flex: 1, padding: 12, borderRadius: 7, border: "none", font: `700 13px ${FONT}`, ...(deleteText === "DELETE" ? { background: "#e0457b", color: "#fff", cursor: "pointer" } : { background: "#2a1a1f", color: "#7a5560", cursor: "not-allowed" }) }}>Delete forever</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ label, value, last }: { label: string; value: React.ReactNode; last?: boolean }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", padding: "10px 0", borderBottom: last ? "none" : `1px solid ${C.line}`, marginBottom: last ? 16 : 0 }}>
      <span style={{ color: C.muted, fontSize: 13 }}>{label}</span>
      <span style={{ color: C.text, fontSize: 13 }}>{value}</span>
    </div>
  );
}

const modalBackdrop: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,.7)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 9999,
};
